<script setup>
import medal1st from '@manseImages/nangman-naming/medal-1st.png';
import medal2nd from '@manseImages/nangman-naming/medal-2nd.png';
import medal3rd from '@manseImages/nangman-naming/medal-3rd.png';
import medal4th from '@manseImages/nangman-naming/medal-4th.png';
import medal5th from '@manseImages/nangman-naming/medal-5th.png';
import iconThumbUp from '@manseImages/nangman-naming/icon-thumb-up.svg?url';
import iconEssay from '@manseImages/nangman-naming/icon-essay.svg?url';
import iconMessage from '@manseImages/nangman-naming/icon-message.svg?url';
import avatarPerson from '@manseImages/nangman-naming/avatar-person.png';
import avatarPetAnimal from '@manseImages/nangman-naming/avatar-pet-animal.png';
import avatarPetPlant from '@manseImages/nangman-naming/avatar-pet-plant.png';
import avatarPetObject from '@manseImages/nangman-naming/avatar-pet-object.png';
import avatarBiz from '@manseImages/nangman-naming/avatar-biz.png';
import avatarCulture from '@manseImages/nangman-naming/avatar-culture.png';
import PopRecommendCriteria from './PopRecommendCriteria.vue';
import { getOhaeng } from '@/utils/ohaengPalette';
import { getNamingRulePalette } from '@/utils/namingRulePalette';
import { buildNamingInputRows } from '../namingFieldConfigs';

/**
 * 모드 별 우측 패널 아바타 — target × pet detail 매핑.
 * person(kor/han)·culture(aho/nickname)는 target 단위로 공유, pet은 detail로 분기.
 */
const MODE_AVATARS = {
  'person-kor': avatarPerson,
  'person-han': avatarPerson,
  'pet-animal': avatarPetAnimal,
  'pet-plant': avatarPetPlant,
  'pet-object': avatarPetObject,
  'biz-biz': avatarBiz,
  'culture-aho': avatarCulture,
  'culture-nickname': avatarCulture,
};

const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));

const props = defineProps({
  /** 대상 ('person' | 'pet' | 'biz' | 'culture') — 데이터 분기용 */
  target: { type: String, default: null },
  /** 세부 옵션 ('kor' | 'han' 등) — 데이터 분기용 */
  detail: { type: String, default: null },
  /** PopNamingDetailInput 결과 */
  detailInput: { type: Object, default: null },
  /** 작명 기준 사주 */
  saju: { type: Object, default: null },
  /** 추천 이름 리스트 — 각 항목에 우측 detail 데이터 포함 */
  recommendList: {
    type: Array,
    default: () => [
      {
        name: '김솔비',
        ohaeng: ['금', '화'],
        matched: true,
        score: 95,
        grade: 'A',
        fromOhaeng: '금',
        toOhaeng: '화',
        scoreItems: [
          { key: 'yongsin', label: '용신', value: 38, max: 40 },
          { key: 'samwon', label: '삼원오행', value: 36, max: 40 },
          { key: 'johu', label: '조후', value: 9, max: 10 },
          { key: 'ipmyo', label: '입묘', value: 6, max: 6 },
          { key: 'gongmang', label: '공망', value: 4, max: 4 },
        ],
        categories: [
          {
            key: 'interpret',
            label: '낭만 해석',
            icon: 'essay',
            headerBg: '#f7f3ee',
            contentBg: 'rgba(247, 243, 238, 0.4)',
            lines: [
              `'솔'은 소나무처럼 꿋꿋한 金의 기운, '비'는 밝게 빛나는 火의 생기를 담고 있어요. `,
              '용신 金이 이름 첫 글자에 정확히 자리잡아 사주의 약점을 직접 보완해요.',
            ],
          },
          {
            key: 'yongsin',
            label: '용신',
            headerBg: '#fdeeee',
            contentBg: 'rgba(253, 238, 238, 0.4)',
            value: 38,
            max: 40,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '金 용신 1자 일치',
            desc: '이름 첫 글자 솔이 용신 金을 직접 담고 있어 부족한 기운을 강하게 보충합니다.',
          },
          {
            key: 'samwon',
            label: '삼원오행',
            headerBg: '#f2f8ef',
            contentBg: 'rgba(242, 248, 239, 0.4)',
            value: 36,
            max: 40,
            grade: '상',
            gradeColor: '#376ac9',
            title: '金→火 상극 주의, 전체 흐름 양호',
            desc: '金과 火는 상극이지만 강한 에너지 발산으로 역동적인 기운을 만듭니다.',
          },
          {
            key: 'johu',
            label: '조후',
            headerBg: '#eef3fb',
            contentBg: 'rgba(238, 243, 251, 0.4)',
            value: 9,
            max: 10,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '계절 기운 보충 부합',
            desc: '임신(壬申)월 출생으로 필요한 金 오행이 이름에 정확히 반영됩니다.',
          },
          {
            key: 'ipmyo',
            label: '입묘',
            headerBg: '#fef7e8',
            contentBg: 'rgba(254, 247, 232, 0.4)',
            value: 6,
            max: 6,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '위험 없음',
            desc: '金과 火는 입묘 관계가 아니라 기운이 자유롭게 발현됩니다.',
          },
          {
            key: 'gongmang',
            label: '공망',
            headerBg: '#f2f2f2',
            contentBg: 'rgba(242, 242, 242, 0.4)',
            value: 4,
            max: 4,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '공망 오 · 미와 충돌 없음',
            desc: '이름 오행 金 · 火가 공망 오(火 · 土)와 직접 충돌하지 않습니다.',
          },
        ],
      },
      {
        name: '김하율',
        ohaeng: ['화', '목'],
        matched: false,
        score: 88,
        grade: 'A',
        fromOhaeng: '화',
        toOhaeng: '목',
        scoreItems: [
          { key: 'yongsin', label: '용신', value: 30, max: 40 },
          { key: 'samwon', label: '삼원오행', value: 34, max: 40 },
          { key: 'johu', label: '조후', value: 8, max: 10 },
          { key: 'ipmyo', label: '입묘', value: 6, max: 6 },
          { key: 'gongmang', label: '공망', value: 4, max: 4 },
        ],
        categories: [
          {
            key: 'interpret',
            label: '낭만 해석',
            icon: 'essay',
            headerBg: '#f7f3ee',
            contentBg: 'rgba(247, 243, 238, 0.4)',
            lines: [
              `'하'는 火의 환한 생기, '율'은 木의 자라남을 품은 이름이에요.`,
              '용신 金과는 직접 닿지 않지만 생기와 성장을 부각한 구성이에요.',
            ],
          },
          {
            key: 'yongsin',
            label: '용신',
            headerBg: '#fdeeee',
            contentBg: 'rgba(253, 238, 238, 0.4)',
            value: 30,
            max: 40,
            grade: '중',
            gradeColor: '#f3ae11',
            title: '용신 직접 일치 없음',
            desc: '용신 金이 이름에 직접 들어있지는 않지만 보조 오행으로 보완됩니다.',
          },
          {
            key: 'samwon',
            label: '삼원오행',
            headerBg: '#f2f8ef',
            contentBg: 'rgba(242, 248, 239, 0.4)',
            value: 34,
            max: 40,
            grade: '상',
            gradeColor: '#376ac9',
            title: '火→木 상생 흐름',
            desc: '火가 木을 키우는 자연스러운 상생 구조로 안정적인 흐름이에요.',
          },
          {
            key: 'johu',
            label: '조후',
            headerBg: '#eef3fb',
            contentBg: 'rgba(238, 243, 251, 0.4)',
            value: 8,
            max: 10,
            grade: '상',
            gradeColor: '#376ac9',
            title: '계절 기운 양호',
            desc: '미월 출생에 필요한 따뜻한 기운이 어느 정도 담겨 있어요.',
          },
          {
            key: 'ipmyo',
            label: '입묘',
            headerBg: '#fef7e8',
            contentBg: 'rgba(254, 247, 232, 0.4)',
            value: 6,
            max: 6,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '위험 없음',
            desc: '火와 木은 입묘 관계가 아니라 기운이 자유롭게 발현됩니다.',
          },
          {
            key: 'gongmang',
            label: '공망',
            headerBg: '#f2f2f2',
            contentBg: 'rgba(242, 242, 242, 0.4)',
            value: 4,
            max: 4,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '공망과 충돌 없음',
            desc: '이름 오행 火 · 木이 공망과 직접 충돌하지 않습니다.',
          },
        ],
      },
      {
        name: '김단이',
        ohaeng: ['화'],
        matched: false,
        score: 85,
        grade: 'A',
        fromOhaeng: '화',
        toOhaeng: '화',
        scoreItems: [
          { key: 'yongsin', label: '용신', value: 28, max: 40 },
          { key: 'samwon', label: '삼원오행', value: 32, max: 40 },
          { key: 'johu', label: '조후', value: 9, max: 10 },
          { key: 'ipmyo', label: '입묘', value: 6, max: 6 },
          { key: 'gongmang', label: '공망', value: 4, max: 4 },
        ],
        categories: [
          {
            key: 'interpret',
            label: '낭만 해석',
            icon: 'essay',
            headerBg: '#f7f3ee',
            contentBg: 'rgba(247, 243, 238, 0.4)',
            lines: [
              `'단'은 火의 단단하고 정돈된 기운, '이'는 가볍게 흐르는 마무리예요.`,
              '단정하고 또렷한 인상을 주는 구성이에요.',
            ],
          },
          {
            key: 'yongsin',
            label: '용신',
            headerBg: '#fdeeee',
            contentBg: 'rgba(253, 238, 238, 0.4)',
            value: 28,
            max: 40,
            grade: '중',
            gradeColor: '#f3ae11',
            title: '용신 직접 일치 없음',
            desc: '용신 金이 이름에 직접 들어있지는 않습니다.',
          },
          {
            key: 'samwon',
            label: '삼원오행',
            headerBg: '#f2f8ef',
            contentBg: 'rgba(242, 248, 239, 0.4)',
            value: 32,
            max: 40,
            grade: '상',
            gradeColor: '#376ac9',
            title: '단일 오행 구성',
            desc: '火 단일 오행 구성으로 인상은 강하지만 균형은 다소 부족합니다.',
          },
          {
            key: 'johu',
            label: '조후',
            headerBg: '#eef3fb',
            contentBg: 'rgba(238, 243, 251, 0.4)',
            value: 9,
            max: 10,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '계절 기운 보충 부합',
            desc: '미월 출생에 필요한 火 기운이 충분히 반영됩니다.',
          },
          {
            key: 'ipmyo',
            label: '입묘',
            headerBg: '#fef7e8',
            contentBg: 'rgba(254, 247, 232, 0.4)',
            value: 6,
            max: 6,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '위험 없음',
            desc: '火는 입묘 관계로 작용하지 않아 기운이 자유롭게 발현됩니다.',
          },
          {
            key: 'gongmang',
            label: '공망',
            headerBg: '#f2f2f2',
            contentBg: 'rgba(242, 242, 242, 0.4)',
            value: 4,
            max: 4,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '공망과 충돌 없음',
            desc: '이름 오행 火가 공망과 직접 충돌하지 않습니다.',
          },
        ],
      },
      {
        name: '김도윤',
        ohaeng: ['토', '금'],
        matched: true,
        score: 82,
        grade: 'B',
        fromOhaeng: '토',
        toOhaeng: '금',
        scoreItems: [
          { key: 'yongsin', label: '용신', value: 32, max: 40 },
          { key: 'samwon', label: '삼원오행', value: 30, max: 40 },
          { key: 'johu', label: '조후', value: 7, max: 10 },
          { key: 'ipmyo', label: '입묘', value: 6, max: 6 },
          { key: 'gongmang', label: '공망', value: 4, max: 4 },
        ],
        categories: [
          {
            key: 'interpret',
            label: '낭만 해석',
            icon: 'essay',
            headerBg: '#f7f3ee',
            contentBg: 'rgba(247, 243, 238, 0.4)',
            lines: [
              `'도'는 土의 묵직한 안정감, '윤'은 金의 매끈한 윤기를 더한 이름이에요.`,
              '土가 金을 생하는 상생 구조라 사주 보강에 무리가 없어요.',
            ],
          },
          {
            key: 'yongsin',
            label: '용신',
            headerBg: '#fdeeee',
            contentBg: 'rgba(253, 238, 238, 0.4)',
            value: 32,
            max: 40,
            grade: '상',
            gradeColor: '#376ac9',
            title: '용신 보조 일치',
            desc: '용신 金이 이름 끝 글자에 자리잡아 부족한 기운을 안정적으로 보완합니다.',
          },
          {
            key: 'samwon',
            label: '삼원오행',
            headerBg: '#f2f8ef',
            contentBg: 'rgba(242, 248, 239, 0.4)',
            value: 30,
            max: 40,
            grade: '중',
            gradeColor: '#f3ae11',
            title: '土→金 상생 흐름',
            desc: '土가 金을 키우는 자연 상생 구조로 안정적인 흐름이지만 강도는 보통입니다.',
          },
          {
            key: 'johu',
            label: '조후',
            headerBg: '#eef3fb',
            contentBg: 'rgba(238, 243, 251, 0.4)',
            value: 7,
            max: 10,
            grade: '상',
            gradeColor: '#376ac9',
            title: '계절 기운 보충 양호',
            desc: '미월 출생에 필요한 기운이 일부 반영되어 있어요.',
          },
          {
            key: 'ipmyo',
            label: '입묘',
            headerBg: '#fef7e8',
            contentBg: 'rgba(254, 247, 232, 0.4)',
            value: 6,
            max: 6,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '위험 없음',
            desc: '土와 金은 입묘 관계가 아니라 기운이 자유롭게 발현됩니다.',
          },
          {
            key: 'gongmang',
            label: '공망',
            headerBg: '#f2f2f2',
            contentBg: 'rgba(242, 242, 242, 0.4)',
            value: 4,
            max: 4,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '공망과 충돌 없음',
            desc: '이름 오행 土 · 金이 공망과 직접 충돌하지 않습니다.',
          },
        ],
      },
      {
        name: '김서준',
        ohaeng: ['수', '목'],
        matched: false,
        score: 78,
        grade: 'B',
        fromOhaeng: '수',
        toOhaeng: '목',
        scoreItems: [
          { key: 'yongsin', label: '용신', value: 26, max: 40 },
          { key: 'samwon', label: '삼원오행', value: 30, max: 40 },
          { key: 'johu', label: '조후', value: 8, max: 10 },
          { key: 'ipmyo', label: '입묘', value: 6, max: 6 },
          { key: 'gongmang', label: '공망', value: 4, max: 4 },
        ],
        categories: [
          {
            key: 'interpret',
            label: '낭만 해석',
            icon: 'essay',
            headerBg: '#f7f3ee',
            contentBg: 'rgba(247, 243, 238, 0.4)',
            lines: [
              `'서'는 水의 차분한 흐름, '준'은 木의 곧게 뻗는 기상을 담은 이름이에요.`,
              '水가 木을 키우는 상생 구조로 부드럽고 안정적인 인상을 줍니다.',
            ],
          },
          {
            key: 'yongsin',
            label: '용신',
            headerBg: '#fdeeee',
            contentBg: 'rgba(253, 238, 238, 0.4)',
            value: 26,
            max: 40,
            grade: '중',
            gradeColor: '#f3ae11',
            title: '용신 직접 일치 없음',
            desc: '용신 金이 이름에 직접 들어있지 않아 보충력은 다소 약합니다.',
          },
          {
            key: 'samwon',
            label: '삼원오행',
            headerBg: '#f2f8ef',
            contentBg: 'rgba(242, 248, 239, 0.4)',
            value: 30,
            max: 40,
            grade: '중',
            gradeColor: '#f3ae11',
            title: '水→木 상생 흐름',
            desc: '水가 木을 자라게 하는 상생 구조로 무난한 흐름이에요.',
          },
          {
            key: 'johu',
            label: '조후',
            headerBg: '#eef3fb',
            contentBg: 'rgba(238, 243, 251, 0.4)',
            value: 8,
            max: 10,
            grade: '상',
            gradeColor: '#376ac9',
            title: '계절 기운 양호',
            desc: '미월 출생에 필요한 水 기운이 어느 정도 담겨 있어요.',
          },
          {
            key: 'ipmyo',
            label: '입묘',
            headerBg: '#fef7e8',
            contentBg: 'rgba(254, 247, 232, 0.4)',
            value: 6,
            max: 6,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '위험 없음',
            desc: '水와 木은 입묘 관계가 아니라 기운이 자유롭게 발현됩니다.',
          },
          {
            key: 'gongmang',
            label: '공망',
            headerBg: '#f2f2f2',
            contentBg: 'rgba(242, 242, 242, 0.4)',
            value: 4,
            max: 4,
            grade: '최상',
            gradeColor: '#53b82c',
            title: '공망과 충돌 없음',
            desc: '이름 오행 水 · 木이 공망과 직접 충돌하지 않습니다.',
          },
        ],
      },
    ],
  },
  /** 명리 총평 문단 (좌측 컬럼) — 부모 주입, 없으면 미표시 */
  summary: {
    type: Array,
    default: () => [],
  },
  /** 추천기준보기 "적용 규칙" — 응답 적용규칙.규칙목록 어댑터 결과. 비면 섹션 미표시(폴백 없음) */
  criteriaRules: {
    type: Array,
    default: () => [],
  },
  /** 추천기준보기 "작명 기준"의 응답 꼬리 행(용신오행/채점기준). 입력값 행은 detailInput으로 생성 */
  criteriaTailRows: {
    type: Array,
    default: () => [],
  },
  /** 추천기준보기 "내 사주" 천간지지 4기둥 (fastSearch birthganji 변환). 비면 섹션 숨김 */
  sajuPillars: {
    type: Array,
    default: () => [],
  },
  /** 추천기준보기 "내 사주" 핵심 오행 태그(용신/희신/한신/기신/구신·조후·공망·격국). 응답 사주정보 기반 */
  sajuTags: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['openCriteria', 'selectName']);

/** 현재 모드 키 (target-detail) */
const modeKey = computed(() => `${props.target}-${props.detail}`);

/** 부모(DetailPageNaming)가 어댑터 변환 결과로 주입한 추천 리스트 */
const displayList = computed(() => props.recommendList || []);

/** 우측 패널 아바타 (모드 별) — 기본값은 사람 */
const modeAvatar = computed(() => MODE_AVATARS[modeKey.value] || avatarPerson);

/** 우측 컬럼에 표시할 현재 선택된 후보 (기본: 1위) */
const selectedIndex = ref(0);
const selectedItem = computed(() => displayList.value[selectedIndex.value] || displayList.value[0]);

const rightPanelRef = ref(null);

const selectByIndex = (idx) => {
  selectedIndex.value = idx;
  emit('selectName', displayList.value[idx]);
  // 카드 전환 시 우측 패널 스크롤을 맨 위로
  nextTick(() => {
    rightPanelRef.value?.scrollTo({ top: 0, behavior: 'smooth' });
  });
};

/** 1~5위 메달 (최대 5개 추천까지 대응) */
const medalMap = [medal1st, medal2nd, medal3rd, medal4th, medal5th];

const progressPercent = (item) => {
  if (!item.max) return 0;
  return Math.min(100, Math.round((item.value / item.max) * 100));
};

const showCriteriaPopup = ref(false);

const openCriteria = () => {
  showCriteriaPopup.value = true;
  emit('openCriteria');
};

const closeCriteria = () => {
  showCriteriaPopup.value = false;
};

/**
 * 추천기준보기 "작명 기준" rows.
 * 실데이터(criteriaRules)가 있으면 사용자 입력값(detailInput) + 응답 꼬리행(용신오행·채점기준),
 * 없으면 빈 배열(폴백 없음 → 섹션 미표시).
 * (ContentsRecommend01은 추천 전용이라 modeKey 앞에 recommend- 접두사를 붙인다.)
 */
const criteriaRowsForPopup = computed(() => {
  if (!props.criteriaRules.length) return [];
  return [
    ...buildNamingInputRows(`recommend-${props.target}-${props.detail}`, props.detailInput),
    ...props.criteriaTailRows,
  ];
});
</script>

<template>
  <VRow v-if="displayList.length" class="full-height naming-row">
    <!-- 좌측 컬럼: 추천 이름 + 명리 총평 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <div class="naming-card-wrap right">
        <div class="naming-left">
          <div class="naming-left__header">
            <div class="naming-left__title-wrap">
              <img
                :src="iconThumbUp"
                alt="추천"
                class="naming-left__title-icon"
                width="26"
                height="26"
              />
              <h3 class="naming-left__title">추천 이름</h3>
            </div>
            <button type="button" class="naming-left__criteria" @click="openCriteria">
              추천 기준 보기
            </button>
          </div>

          <p class="naming-left__sub">입력하신 정보로 아래와 같이 추천하였습니다.</p>

          <ul class="naming-left__list">
            <li
              v-for="(item, idx) in displayList"
              :key="item.name + idx"
              class="naming-card"
              :class="{ 'naming-card--active': idx === selectedIndex }"
              @click="selectByIndex(idx)"
            >
              <div class="naming-card__medal">
                <img :src="medalMap[idx]" :alt="`${idx + 1}위 메달`" />
              </div>
              <div class="naming-card__body">
                <p class="naming-card__name">
                  {{ item.name }}<span v-if="item.hangul" class="naming-card__hangul">{{ item.hangul }}</span>
                </p>
                <div class="naming-card__meta">
                  <div class="naming-card__ohaeng-group">
                    <template v-for="(oh, ohIdx) in item.ohaeng" :key="oh + ohIdx">
                      <span
                        class="naming-card__ohaeng"
                        :style="{
                          color: getOhaeng(oh).color,
                          background: getOhaeng(oh).bg,
                        }"
                      >
                        {{ oh }}
                      </span>
                      <span
                        v-if="ohIdx < item.ohaeng.length - 1"
                        class="naming-card__dot"
                      >·</span>
                    </template>
                  </div>
                  <span v-if="item.matched" class="naming-card__matched">용신 일치</span>
                </div>
              </div>
              <div class="naming-card__score-wrap">
                <span class="naming-card__score">{{ item.score }}점</span>
                <span class="naming-card__grade">{{ item.grade }}</span>
              </div>
            </li>
          </ul>

          <section v-if="summary.length" class="naming-left__summary">
            <header class="naming-left__summary-head">
              <img :src="iconMessage" alt="" class="naming-left__summary-icon" width="24" height="24" />
              <span class="naming-left__summary-title">명리총평</span>
            </header>
            <div class="naming-left__summary-box">
              <p v-for="(line, i) in summary" :key="i" class="naming-left__summary-line">
                {{ line }}
              </p>
            </div>
          </section>
        </div>
      </div>
    </VCol>

    <!-- 우측 컬럼: 선택된 후보 분석 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <div ref="rightPanelRef" class="naming-card-wrap left">
        <div class="naming-right">
          <!-- 헤더 -->
          <div class="ndp-header">
            <div class="ndp-header__avatar">
              <img :src="modeAvatar" alt="" />
            </div>
            <div class="ndp-header__body">
              <p class="ndp-header__name">
                {{ selectedItem.name }}<span v-if="selectedItem.hangul" class="ndp-header__hangul">{{ selectedItem.hangul }}</span>
              </p>
            </div>
            <div class="ndp-header__score-wrap">
              <span class="ndp-header__score">{{ selectedItem.score }}점</span>
              <span class="ndp-header__grade">{{ selectedItem.grade }}</span>
            </div>
          </div>

          <!-- 오행 변환 + progress -->
          <section class="ndp-summary">
            <div class="ndp-ohaeng-transform">
              <template v-for="(oh, i) in selectedItem.ohaeng" :key="`oh-${oh}-${i}`">
                <div class="ndp-ohaeng-box" :style="{ background: getOhaeng(oh).solid }">
                  <span>{{ oh }}</span>
                </div>
                <span
                  v-if="i < selectedItem.ohaeng.length - 1"
                  class="ndp-ohaeng-sep"
                >·</span>
              </template>
            </div>

            <ul class="ndp-progress-list">
              <li v-for="item in selectedItem.scoreItems" :key="item.key" class="ndp-progress">
                <span class="ndp-progress__label">{{ item.label }}</span>
                <div class="ndp-progress__bar">
                  <span
                    class="ndp-progress__fill"
                    :style="{ width: progressPercent(item) + '%', background: getNamingRulePalette(item.label).color }"
                  />
                </div>
                <span class="ndp-progress__value">
                  <strong>{{ item.value }}</strong>
                  <span class="ndp-progress__max">/{{ item.max }}</span>
                </span>
              </li>
            </ul>
          </section>

          <!-- 카테고리별 상세 -->
          <section class="ndp-categories">
            <div v-for="cat in selectedItem.categories" :key="cat.key" class="ndp-cat">
              <div
                class="ndp-cat__header"
                :class="{ 'ndp-cat__header--with-icon': cat.icon === 'essay' }"
                :style="{ background: cat.headerBg }"
              >
                <div class="ndp-cat__header-left">
                  <img
                    v-if="cat.icon === 'essay'"
                    :src="iconEssay"
                    alt=""
                    class="ndp-cat__icon"
                    width="24"
                    height="24"
                  />
                  <span class="ndp-cat__title">{{ cat.label }}</span>
                </div>
                <div v-if="cat.value != null" class="ndp-cat__header-right">
                  <span class="ndp-cat__score">
                    <strong>{{ cat.value }}</strong>
                    <span class="ndp-cat__score-max">/{{ cat.max }}</span>
                  </span>
                  <span class="ndp-cat__grade" :style="{ color: cat.gradeColor }">
                    {{ cat.grade }}
                  </span>
                </div>
              </div>
              <div
                v-if="cat.lines || cat.title || cat.desc"
                class="ndp-cat__body"
                :style="{ background: cat.contentBg }"
              >
                <template v-if="cat.lines">
                  <p v-for="(line, i) in cat.lines" :key="i" class="ndp-cat__line">
                    {{ line }}
                  </p>
                </template>
                <template v-else>
                  <p v-if="cat.title" class="ndp-cat__line ndp-cat__line--title">
                    {{ cat.title }}
                  </p>
                  <p v-if="cat.desc" class="ndp-cat__line">{{ cat.desc }}</p>
                </template>
              </div>
            </div>
          </section>
        </div>
      </div>
    </VCol>
  </VRow>

  <PopRecommendCriteria
    :is-visible="showCriteriaPopup"
    :saju="{ pillars: sajuPillars, tags: sajuTags }"
    :criteria="{ rows: criteriaRowsForPopup }"
    :rules="criteriaRules"
    @close="closeCriteria"
  />
</template>

<style lang="scss" scoped>
@import '@/assets/nangman_style/_analysis_main_color.scss';

$naming-accent-bright: #714ce5;

.naming-row {
  margin: 0;
}

.naming-card-wrap {
  height: 100%;
  background: #fff;
  overflow-y: auto;

  &.right {
    border-right: 1px solid #ebebeb;
  }

  &.left {
    border-left: 1px solid #ebebeb;
  }
}

/* ================== 좌측 컬럼 ================== */
.naming-left {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  padding: 32px 40px;
  box-sizing: border-box;
}

.naming-left__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.naming-left__title-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.naming-left__title-icon {
  display: block;
  width: 26px;
  height: 26px;
  object-fit: contain;
  flex-shrink: 0;
}

.naming-left__title {
  margin: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.naming-left__criteria {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border: none;
  border-radius: 100px;
  background: $naming-accent-bright;
  color: #fff;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  cursor: pointer;

  &:hover { opacity: 0.9; }
}

.naming-left__sub {
  margin: 0;
  padding: 0 5px;
  color: #575757;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.3;
}

.naming-left__list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.naming-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  border: 1.5px solid #ebebeb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &--active {
    border-color: $naming-accent-bright;
  }

  &:hover {
    border-color: $naming-accent-bright;
  }
}

.naming-card__medal {
  flex-shrink: 0;
  width: 50px;
  height: 50px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
}

.naming-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.naming-card__name {
  margin: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.naming-card__hangul {
  margin-left: 4px;
  color: #999;
  font-size: 14px;
  font-weight: 400;
}

.naming-card__meta {
  display: flex;
  align-items: center;
  gap: 5px;
}

.naming-card__ohaeng-group {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.naming-card__ohaeng {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  padding: 3px 6px;
  border-radius: 4px;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
  line-height: 15px;
  box-sizing: border-box;
}

.naming-card__dot {
  color: #575757;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
}

.naming-card__matched {
  color: #575757;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.3;
}

.naming-card__score-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.naming-card__score {
  color: #333;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

.naming-card__grade {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 3px 6px;
  border-radius: 4px;
  background: rgba(116, 183, 90, 0.1);
  color: #74b75a;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
  line-height: 12px;
  box-sizing: border-box;
}

.naming-left__summary {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.naming-left__summary-head {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 40px;
  padding: 8px 16px;
  background: #fff4ed;
  box-sizing: border-box;
}

.naming-left__summary-icon {
  display: block;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.naming-left__summary-title {
  color: #333;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
}

.naming-left__summary-box {
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  background: rgba(255, 244, 237, 0.4);
}

.naming-left__summary-line {
  margin: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  word-break: keep-all;
}

/* ================== 우측 컬럼 ================== */
.naming-right {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 20px;
  padding: 32px 40px;
  box-sizing: border-box;
}

.ndp-header {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 93px;
  padding: 15px;
  border: 1.5px solid #ebebeb;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
}

.ndp-header__avatar {
  flex-shrink: 0;
  width: 50px;
  height: 50px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
}

.ndp-header__body {
  flex: 1;
  min-width: 0;
}

.ndp-header__name {
  margin: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.ndp-header__hangul {
  margin-left: 4px;
  color: #999;
  font-size: 14px;
  font-weight: 400;
}

.ndp-header__score-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.ndp-header__score {
  color: #333;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

.ndp-header__grade {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 3px 6px;
  border-radius: 4px;
  background: rgba(116, 183, 90, 0.1);
  color: #74b75a;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
  line-height: 12px;
  box-sizing: border-box;
}

.ndp-summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px 0 20px;
  border-radius: 12px;
}

.ndp-ohaeng-transform {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.ndp-ohaeng-box {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  color: #fff;
  font-family: 'ChosunGs', serif;
  font-size: 22px;
  font-weight: 400;
  line-height: 1.2;
}

.ndp-ohaeng-sep {
  display: inline-flex;
  align-self: stretch;
  align-items: center;
  color: #575757;
  font-family: Pretendard;
  font-size: 28px;
  font-weight: 500;
  line-height: 1;
}

.ndp-progress-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  list-style: none;
  margin: 30px 0 0;
  padding: 0;
  width: 100%;
}

.ndp-progress {
  display: grid;
  grid-template-columns: 60px 1fr 50px;
  align-items: center;
  gap: 15px;
}

.ndp-progress__label {
  color: #575757;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
}

.ndp-progress__bar {
  position: relative;
  width: 100%;
  height: 10px;
  border-radius: 10px;
  background: #ebebeb;
  overflow: hidden;
}

.ndp-progress__fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 10px;
  transition: width 0.3s ease;
}

.ndp-progress__value {
  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 1px;
  text-align: right;

  strong {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
  }
}

.ndp-progress__max {
  color: #757575;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.3;
}

.ndp-categories {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.ndp-cat {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.ndp-cat__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  height: 40px;
  padding: 8px 24px;
  box-sizing: border-box;

  &--with-icon {
    padding-left: 16px;
  }
}

.ndp-cat__header-left {
  display: flex;
  align-items: center;
  gap: 5px;
}

.ndp-cat__icon {
  width: 24px;
  height: 24px;
  display: block;
  flex-shrink: 0;
}

.ndp-cat__title {
  color: #333;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
}

.ndp-cat__header-right {
  display: inline-flex;
  align-items: center;
  gap: 15px;
}

.ndp-cat__score {
  display: inline-flex;
  align-items: baseline;
  gap: 1px;

  strong {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.3;
  }
}

.ndp-cat__score-max {
  color: #757575;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.3;
}

.ndp-cat__grade {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  padding: 5px 6px;
  border-radius: 4px;
  background: #fff;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
  line-height: 12px;
}

.ndp-cat__body {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 16px 24px;
}

.ndp-cat__line {
  margin: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  word-break: keep-all;

  &--title {
    font-size: 16px;
    font-weight: 500;
  }
}
</style>
